import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI, panAPI, aadhaarAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, CheckCircle, UserPlus, Users, Edit, AlertTriangle, CreditCard, XCircle, Clock, Trash2, QrCode, Fingerprint } from 'lucide-react';
import { BackButton, PANPhotoUpload } from '../components/common';
import AadhaarQRScanner from '../components/AadhaarQRScanner';

export const KYCPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    address: user?.address || '',
    aadhaar_number: user?.aadhaar_number || '',
    has_nominee: user?.has_nominee || false,
    nominee_name: user?.nominee_name || '',
    nominee_relationship: user?.nominee_relationship || '',
    nominee_aadhaar: user?.nominee_aadhaar || '',
    terms_accepted: false,
  });

  // PAN KYC State
  const [panStatus, setPanStatus] = useState(null);
  const [panLoading, setPanLoading] = useState(false);
  const [panSubmitting, setPanSubmitting] = useState(false);
  const [panNumber, setPanNumber] = useState('');
  const [panPhoto, setPanPhoto] = useState(null);
  const [showPanForm, setShowPanForm] = useState(false);

  // Aadhaar QR Scanner State
  const [showAadhaarScanner, setShowAadhaarScanner] = useState(false);
  const [aadhaarStatus, setAadhaarStatus] = useState(null);
  const [aadhaarLoading, setAadhaarLoading] = useState(false);

  // Fetch Aadhaar status on mount
  useEffect(() => {
    const fetchAadhaarStatus = async () => {
      setAadhaarLoading(true);
      try {
        const response = await aadhaarAPI.getStatus();
        setAadhaarStatus(response.data);
      } catch (error) {
        // Not verified yet - that's ok
        if (error.response?.status !== 404) {
          console.error('Failed to fetch Aadhaar status:', error);
        }
      } finally {
        setAadhaarLoading(false);
      }
    };
    fetchAadhaarStatus();
  }, []);

  // Fetch PAN status on mount
  useEffect(() => {
    const fetchPanStatus = async () => {
      setPanLoading(true);
      try {
        const response = await panAPI.getStatus();
        setPanStatus(response.data);
      } catch (error) {
        // PAN not submitted yet - that's ok
        if (error.response?.status !== 404) {
          console.error('Failed to fetch PAN status:', error);
        }
      } finally {
        setPanLoading(false);
      }
    };
    fetchPanStatus();
  }, []);

  // Handle PAN submission
  const handlePanSubmit = async () => {
    if (!panNumber || panNumber.length !== 10) {
      toast.error('Please enter a valid 10-character PAN number');
      return;
    }
    if (!panPhoto) {
      toast.error('Please upload a photo of your PAN card');
      return;
    }

    setPanSubmitting(true);
    try {
      await panAPI.submit(panNumber.toUpperCase(), panPhoto);
      toast.success('PAN submitted successfully! Awaiting verification.');
      // Refresh PAN status
      const response = await panAPI.getStatus();
      setPanStatus(response.data);
      setShowPanForm(false);
      setPanNumber('');
      setPanPhoto(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit PAN');
    } finally {
      setPanSubmitting(false);
    }
  };

  // Handle PAN deletion (right to erasure)
  const handlePanDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your PAN details? This action cannot be undone.')) {
      return;
    }

    setPanLoading(true);
    try {
      await panAPI.delete();
      toast.success('PAN details deleted successfully');
      setPanStatus(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete PAN');
    } finally {
      setPanLoading(false);
    }
  };

  // Handle Aadhaar QR verification completion
  const handleAadhaarVerificationComplete = async (result) => {
    setShowAadhaarScanner(false);
    await refreshUser();
    // Refresh Aadhaar status
    try {
      const response = await aadhaarAPI.getStatus();
      setAadhaarStatus(response.data);
    } catch (error) {
      console.error('Failed to refresh Aadhaar status:', error);
    }
    // Navigate to profile if KYC is now complete
    if (result?.data?.aadhaar_last_4) {
      navigate('/profile');
    }
  };

  // Format PAN number input
  const handlePanNumberChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    setPanNumber(value);
  };

  const relationshipOptions = [
    'Spouse',
    'Father',
    'Mother',
    'Son',
    'Daughter',
    'Brother',
    'Sister',
    'Grandfather',
    'Grandmother',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'aadhaar_number' || name === 'nominee_aadhaar') {
      // Format Aadhaar with spaces
      const digits = value.replace(/\D/g, '').slice(0, 12);
      setFormData(prev => ({ ...prev, [name]: digits }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const formatAadhaar = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.name.trim().length < 3) {
      toast.error('Name must be at least 3 characters');
      return;
    }

    if (!nameConfirmed) {
      toast.error('Please confirm that you have verified your name as per Aadhaar');
      return;
    }

    if (formData.aadhaar_number.length !== 12) {
      toast.error('Aadhaar number must be 12 digits');
      return;
    }

    if (!formData.address.trim()) {
      toast.error('Address is required');
      return;
    }

    if (formData.has_nominee) {
      if (!formData.nominee_name || formData.nominee_name.trim().length < 3) {
        toast.error('Nominee name must be at least 3 characters');
        return;
      }
      if (!formData.nominee_relationship) {
        toast.error('Please select nominee relationship');
        return;
      }
      if (formData.nominee_aadhaar && formData.nominee_aadhaar.length !== 12) {
        toast.error('Nominee Aadhaar number must be 12 digits');
        return;
      }
    }

    if (!formData.terms_accepted) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }

    setLoading(true);
    try {
      await userAPI.updateKYC({
        name: formData.name.trim(),
        address: formData.address.trim(),
        aadhaar_number: formData.aadhaar_number,
        email: formData.email?.trim() || null,
        has_nominee: formData.has_nominee,
        nominee_name: formData.has_nominee ? formData.nominee_name.trim() : null,
        nominee_relationship: formData.has_nominee ? formData.nominee_relationship : null,
        nominee_aadhaar: formData.has_nominee ? formData.nominee_aadhaar : null,
        terms_accepted: formData.terms_accepted,
      });
      await refreshUser();
      toast.success('KYC details updated successfully!');
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update KYC details');
    } finally {
      setLoading(false);
    }
  };

  if (user?.kyc_verified) {
    return (
      <div className="min-h-screen bg-cream" data-testid="kyc-page">
        <div className="bg-white border-b border-border px-4 py-4">
          <div className="max-w-lg mx-auto flex items-center gap-4">
            <BackButton />
            <h1 className="font-serif text-xl text-navy font-semibold">KYC Verification</h1>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-8">
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="font-serif text-2xl text-navy font-bold mb-2">KYC Verified</h2>
              <p className="text-muted-foreground mb-6">Your identity has been verified successfully.</p>
              
              <div className="text-left space-y-4 bg-white rounded-xl p-4">
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium text-navy">{user.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aadhaar Number</p>
                  <p className="font-mono text-navy">XXXX XXXX {user.aadhaar_number?.slice(-4)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="text-navy">{user.address}</p>
                </div>
                {user.has_nominee && (
                  <>
                    <div className="pt-4 border-t border-border">
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Nominee Details
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Nominee Name</p>
                      <p className="font-medium text-navy">{user.nominee_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Relationship</p>
                      <p className="text-navy">{user.nominee_relationship}</p>
                    </div>
                    {user.nominee_aadhaar && (
                      <div>
                        <p className="text-sm text-muted-foreground">Nominee Aadhaar</p>
                        <p className="font-mono text-navy">XXXX XXXX {user.nominee_aadhaar?.slice(-4)}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Edit Basic Details Button */}
              <Button
                onClick={() => navigate('/profile/edit')}
                variant="outline"
                className="mt-6 gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Basic Details
              </Button>
            </CardContent>
          </Card>

          {/* PAN Card Section */}
          <Card className="mt-6 border-gold/30" data-testid="pan-section">
            <CardHeader>
              <CardTitle className="font-serif text-lg text-navy flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gold" />
                PAN Card Verification
              </CardTitle>
              <CardDescription>
                Required for purchases over ₹2,00,000
              </CardDescription>
            </CardHeader>
            <CardContent>
              {panLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gold" />
                </div>
              ) : panStatus?.pan_status === 'verified' ? (
                // PAN Verified
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">PAN Verified</p>
                      <p className="text-sm text-green-700">PAN: XXXXXX{panStatus.pan_last_4}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handlePanDelete}
                    disabled={panLoading}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete PAN Details
                  </Button>
                </div>
              ) : panStatus?.pan_status === 'pending' ? (
                // PAN Pending
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <Clock className="w-8 h-8 text-yellow-600" />
                    <div>
                      <p className="font-medium text-yellow-800">Verification Pending</p>
                      <p className="text-sm text-yellow-700">PAN: XXXXXX{panStatus.pan_last_4}</p>
                      <p className="text-xs text-yellow-600 mt-1">Your PAN is being verified by our team</p>
                    </div>
                  </div>
                </div>
              ) : panStatus?.pan_status === 'rejected' && !showPanForm ? (
                // PAN Rejected - Show rejection message and resubmit button
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-3">
                      <XCircle className="w-8 h-8 text-red-600" />
                      <div>
                        <p className="font-medium text-red-800">PAN Rejected</p>
                        <p className="text-sm text-red-700">Reason: {panStatus.pan_rejection_reason}</p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setShowPanForm(true)}
                    className="w-full gold-gradient text-navy font-semibold"
                  >
                    Resubmit PAN
                  </Button>
                </div>
              ) : (showPanForm || panStatus?.pan_status === 'rejected') ? (
                // PAN Submission Form
                <div className="space-y-4">
                  {/* Show rejection context if resubmitting */}
                  {panStatus?.pan_status === 'rejected' && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium">Resubmitting PAN</p>
                      <p className="text-xs text-amber-700 mt-1">Previous rejection: {panStatus.pan_rejection_reason}</p>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="panNumber">PAN Number</Label>
                    <Input
                      id="panNumber"
                      value={panNumber}
                      onChange={handlePanNumberChange}
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      className="font-mono uppercase mt-1"
                      data-testid="pan-number-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: 5 letters, 4 digits, 1 letter</p>
                  </div>
                  
                  <div>
                    <Label>PAN Card Photo</Label>
                    <div className="mt-1">
                      <PANPhotoUpload 
                        onPhotoCapture={setPanPhoto}
                        disabled={panSubmitting}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowPanForm(false);
                        setPanNumber('');
                        setPanPhoto(null);
                      }}
                      disabled={panSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handlePanSubmit}
                      disabled={panSubmitting || !panNumber || !panPhoto}
                      className="flex-1 gold-gradient text-navy font-semibold"
                      data-testid="submit-pan-btn"
                    >
                      {panSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit PAN'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                // Not Submitted - Show submit button
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600">
                      PAN verification is required for gold purchases exceeding ₹2,00,000 as per RBI guidelines.
                    </p>
                    {panStatus?.pan_required && (
                      <Badge className="mt-2 bg-orange-100 text-orange-800 border-orange-200">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Required - You have crossed ₹2L threshold
                      </Badge>
                    )}
                  </div>
                  <Button 
                    onClick={() => setShowPanForm(true)}
                    className="w-full gold-gradient text-navy font-semibold"
                    data-testid="add-pan-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Add PAN Card
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="kyc-page">
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <BackButton />
          <h1 className="font-serif text-xl text-navy font-semibold">KYC Verification</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 pb-32">
        {/* Info Banner */}
        <Card className="mb-6 bg-primary/10 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-navy">Why KYC?</p>
                <p className="text-sm text-muted-foreground mt-1">
                  KYC verification helps us secure your account and enables features like gold redemption.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Aadhaar QR Verification Section */}
        {showAadhaarScanner ? (
          <div className="mb-6">
            <AadhaarQRScanner 
              onVerificationComplete={handleAadhaarVerificationComplete}
              onClose={() => setShowAadhaarScanner(false)}
            />
          </div>
        ) : (
          <Card className="mb-6 border-gold/30" data-testid="aadhaar-qr-section">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                  <Fingerprint className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <CardTitle className="font-serif text-lg text-navy">Quick Aadhaar Verification</CardTitle>
                  <CardDescription>Verify instantly using Aadhaar QR code</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {aadhaarLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-gold" />
                </div>
              ) : aadhaarStatus?.status === 'verified' && aadhaarStatus?.kyc_method === 'aadhaar_qr_offline' ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Aadhaar Verified via QR</p>
                    <p className="text-sm text-green-700">XXXX XXXX {aadhaarStatus.aadhaar_last_4}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Scan or upload the QR code from the back of your Aadhaar card for instant verification.
                  </p>
                  <Button 
                    type="button"
                    onClick={() => setShowAadhaarScanner(true)}
                    className="w-full gold-gradient text-navy font-semibold"
                    data-testid="scan-aadhaar-qr-btn"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    Verify with Aadhaar QR
                  </Button>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">
                      Or fill in the form below manually
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          <Card className="shadow-lg border-gold/20 mb-6">
            <CardHeader>
              <CardTitle className="font-serif text-lg text-navy">Personal Details</CardTitle>
              <CardDescription>Please enter your details as per government ID</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Name Field with Mandatory Edit Notice */}
              <div className="space-y-2">
                <Label htmlFor="name">Full Name (as per Aadhaar) *</Label>
                <Input
                  data-testid="name-input"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name as per Aadhaar"
                  className="h-12"
                />
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      <strong>Important:</strong> Please enter your name exactly as it appears on your Aadhaar card. 
                      This name will be used on all gold vouchers and official documents.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    id="name_confirmed"
                    checked={nameConfirmed}
                    onCheckedChange={(checked) => setNameConfirmed(checked)}
                  />
                  <Label 
                    htmlFor="name_confirmed" 
                    className="text-sm cursor-pointer text-muted-foreground"
                  >
                    I confirm that the name above matches my Aadhaar card *
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email (Optional)</Label>
                <Input
                  data-testid="email-input"
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="aadhaar_number">Aadhaar Number *</Label>
                <Input
                  data-testid="aadhaar-input"
                  id="aadhaar_number"
                  name="aadhaar_number"
                  value={formatAadhaar(formData.aadhaar_number)}
                  onChange={handleChange}
                  placeholder="1234 5678 9012"
                  className="h-12 font-mono tracking-wider"
                  maxLength={14}
                />
                <p className="text-xs text-muted-foreground">Enter 12-digit Aadhaar number</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Textarea
                  data-testid="address-input"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter your complete address"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Nominee Section */}
          <Card className="shadow-lg border-gold/20 mb-6">
            <CardHeader>
              <CardTitle className="font-serif text-lg text-navy flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Nominee Details
              </CardTitle>
              <CardDescription>Add a nominee for your gold holdings (optional)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="has_nominee"
                  checked={formData.has_nominee}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, has_nominee: checked }))}
                />
                <Label 
                  htmlFor="has_nominee" 
                  className="text-sm font-medium cursor-pointer"
                >
                  I want to add a nominee
                </Label>
              </div>

              {formData.has_nominee && (
                <div className="space-y-4 pt-4 border-t border-border animate-fade-in">
                  <div className="space-y-2">
                    <Label htmlFor="nominee_name">Nominee Full Name *</Label>
                    <Input
                      data-testid="nominee-name-input"
                      id="nominee_name"
                      name="nominee_name"
                      value={formData.nominee_name}
                      onChange={handleChange}
                      placeholder="Enter nominee's full name"
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nominee_relationship">Relationship *</Label>
                    <Select
                      value={formData.nominee_relationship}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, nominee_relationship: value }))}
                    >
                      <SelectTrigger data-testid="nominee-relationship-select" className="h-12">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {relationshipOptions.map((rel) => (
                          <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nominee_aadhaar">Nominee Aadhaar Number (Optional)</Label>
                    <Input
                      data-testid="nominee-aadhaar-input"
                      id="nominee_aadhaar"
                      name="nominee_aadhaar"
                      value={formatAadhaar(formData.nominee_aadhaar)}
                      onChange={handleChange}
                      placeholder="1234 5678 9012"
                      className="h-12 font-mono tracking-wider"
                      maxLength={14}
                    />
                    <p className="text-xs text-muted-foreground">Enter 12-digit Aadhaar number</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* PAN Card Section - Optional but Required for >₹2L purchases */}
          <Card className="shadow-lg border-gold/20 mb-6" data-testid="pan-section-form">
            <CardHeader>
              <CardTitle className="font-serif text-lg text-navy flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gold" />
                PAN Card (Optional)
              </CardTitle>
              <CardDescription>
                Required for purchases over ₹2,00,000. You can add this later from your profile.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {panLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-gold" />
                </div>
              ) : panStatus?.pan_status === 'verified' ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">PAN Verified</p>
                    <p className="text-sm text-green-700">PAN: XXXXXX{panStatus.pan_last_4}</p>
                  </div>
                </div>
              ) : panStatus?.pan_status === 'pending' ? (
                <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <Clock className="w-6 h-6 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-800">Verification Pending</p>
                    <p className="text-sm text-yellow-700">PAN: XXXXXX{panStatus.pan_last_4}</p>
                  </div>
                </div>
              ) : panStatus?.pan_status === 'rejected' && !showPanForm ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                    <XCircle className="w-6 h-6 text-red-600" />
                    <div>
                      <p className="font-medium text-red-800">PAN Rejected</p>
                      <p className="text-sm text-red-700">{panStatus.pan_rejection_reason}</p>
                    </div>
                  </div>
                  <Button 
                    type="button"
                    onClick={() => setShowPanForm(true)}
                    variant="outline"
                    className="w-full"
                  >
                    Resubmit PAN
                  </Button>
                </div>
              ) : (showPanForm || panStatus?.pan_status === 'rejected') ? (
                <div className="space-y-4">
                  {/* Show rejection context if resubmitting */}
                  {panStatus?.pan_status === 'rejected' && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium">Resubmitting PAN</p>
                      <p className="text-xs text-amber-700 mt-1">Previous rejection: {panStatus.pan_rejection_reason}</p>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="panNumberForm">PAN Number</Label>
                    <Input
                      id="panNumberForm"
                      value={panNumber}
                      onChange={handlePanNumberChange}
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      className="font-mono uppercase mt-1"
                    />
                  </div>
                  <div>
                    <Label>PAN Card Photo</Label>
                    <div className="mt-1">
                      <PANPhotoUpload 
                        onPhotoCapture={setPanPhoto}
                        disabled={panSubmitting}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPanForm(false);
                        setPanNumber('');
                        setPanPhoto(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handlePanSubmit}
                      disabled={panSubmitting || !panNumber || !panPhoto}
                      className="flex-1 gold-gradient text-navy"
                    >
                      {panSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit PAN'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button 
                  type="button"
                  onClick={() => setShowPanForm(true)}
                  variant="outline"
                  className="w-full"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add PAN Card
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Terms and Conditions */}
          <Card className="shadow-lg border-gold/20 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="terms_accepted"
                  checked={formData.terms_accepted}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, terms_accepted: checked }))}
                  className="mt-1"
                />
                <Label 
                  htmlFor="terms_accepted" 
                  className="text-sm cursor-pointer leading-relaxed"
                >
                  I have read and agree to the{' '}
                  <Link to="/terms-new" className="text-primary hover:underline font-medium">
                    Terms & Conditions
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy-new" className="text-primary hover:underline font-medium">
                    Privacy Policy
                  </Link>
                  {' '}of Mint a Milli by Mothers Gold & Diamonds LLP. *
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Fixed Bottom Button */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4">
            <div className="max-w-lg mx-auto">
              <Button
                data-testid="submit-kyc-btn"
                type="submit"
                disabled={loading || !formData.terms_accepted || !nameConfirmed}
                className="w-full h-14 text-lg font-semibold gold-gradient text-navy disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <ShieldCheck className="w-5 h-5 mr-2" />
                )}
                {loading ? 'Saving...' : 'Complete KYC Verification'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KYCPage;
